/**
 * @file        http-bearer.js
 *              Utility code for transports working with http (or https) bearers
 *
 * @author      Wes Garland, wes@kingsds.network
 * @date        Apr 2022
 */
'use strict';
const dcpEnv = require('dcp/common/dcp-env')

const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
const debugging         = require('dcp/debugging').scope('dcp');
const chalk = require('dcp/utils').chalk();

/**
 * Returns true if friendLocation should work in place of location from this host.
 * This allows us to transparently configure inter-daemon communication that uses
 * local LAN IPs instead of bouncing off the firewall for NAT.
 */
exports.a$isFriendlyUrl = async function httpBearer$$isFriendlyUrl(url)
{
  var remoteIp, dnsAddr;
  var ifaces;

  if (!url)
    return false;

  if (url.hostname === 'localhost')
    return true;

  switch(url.protocol)
  {
    case 'http:':
    case 'https:':
    case 'ws:':
    case 'tcp:':
    case 'udp:':
    case 'dcpsaw:':
      break;
    default:
      return false;
  }

  /* Consider same-origin match friendly */
  if (dcpEnv.isBrowserPlatform)
    return url.origin === window.location.origin;

  /* Convert an IP address to a 32-bit int in network order */
  function i32(addr)
  {
    var ret = 0;
    var octets = addr.split('.');

    ret |= octets[0] << 24; /* Note: JS ints are signed 32, but that doesn't matter for masking */
    ret |= octets[1] << 16;
    ret |= octets[2] <<  8;
    ret |= octets[3] <<  0;

    return ret;
  }

  const options = {};
  if (dcpConfig.network.disableIPv6)
    options.family = 4;
  else if (dcpConfig.network.disableIPv4)
    options.family = 6;

  dnsAddr = await requireNative('dns').promises.lookup(url.hostname, options);
  if (!dnsAddr)
    return false;

  /** @todo implement ipv6 subnet test */
  /* IPv6 addresses on loopback network are friendly. */
  if (!dcpConfig.network.disableIPv6)
    return /^::[0-9]+$/.test(dnsAddr);

  if (dcpConfig.network.disableIPv4)
    return false;
  /* Consider machines in same IPv4 subnet friendly */
  remoteIp = i32(dnsAddr.address);
  ifaces = requireNative('os').networkInterfaces();
  for (let ifaceName of Object.keys(ifaces))
  {
    for (let alias of ifaces[ifaceName])
    {
      if (alias.family !== 'IPv4')
        continue;

      let i32_addr = i32(alias.address);
      let i32_mask = i32(alias.netmask);

      if ((i32_addr & i32_mask) === (remoteIp & i32_mask))
        return true;
    }
  }

  return false;
}

/**
 * Factory which create instances of Server for targets/listeners, suitable for binding to
 * transports which use an underlying http (or https) bearer.
 *
 * @param {object}      config                  the configuration object for the listener; must have
 *                                              either a location (or ideally) a listen property which
 *                                              is an instance of URL or DcpURL.
 * @param {function}    listeningHandler        [optional] a function which is invoked as soon as the
 *                                              underlying server is listening for new connections. It
 *                                              receives as its sole argument an instance of Server.
 */
exports.httpServerFactory = function(config, listeningHandler)
{
  var bearer;
  var listen = config.listen || config.location;

  const listenOpts = {
    host: listen.hostname,
    port: listen.port
  };

  switch (listen.protocol)
  {
    case 'http:':
      bearer = requireNative('http');
      listenOpts.port = listenOpts.port || 80;
      break;
    case 'https:':
      bearer = requireNative('https');
      listenOpts.port = listenOpts.port || 443;
      break;
    default:
      throw new Error(`unknown bearer: '${listen.protocol}'`)
  }

  switch(listenOpts.host)
  {
    case 'INADDR_ANY':
    case 'inaddr_any':
    case 'any/0':
    case '0.0.0.0':
      listenOpts.host = '::';  /* transparently support IPv4 and IPv6 for inaddr_any */
      break;
  }

  debugging('http') && console.debug('creating http server for', listenOpts);
  const server = bearer.createServer();

  /* Add events debugging by intercepting server.emit */
  if (debugging('events'))
  {
    const net = requireNative('net');
    const inspect = (dcpEnv.platform === 'nodejs') ? requireNative('util').inspect.custom : Symbol('inspect');
    const _emit = server.emit;

    server.emit = function emitWrapper() {
      var disp;
      if (arguments[1] === undefined)
        disp = '';
      else if (arguments[1][inspect])
        disp = arguments[1];
      else if (arguments[1] instanceof net.Socket)
        disp = '';
      else if (arguments[1] instanceof net.Server)
        disp = arguments[1]._connectionKey;
      else if (arguments[1].method)
        disp = `${arguments[1].method} ${arguments[1].url}`;
      else
        disp = Array.from(arguments).slice(1);

      debugging('events') && console.debug('http server', chalk.cyan('fired event: ' + arguments[0]), disp);
      _emit.apply(server, arguments);
    };
  }

  server.listen(listenOpts, () => {
    debugging() && console.debug('http server listening on', server.address().address + ':' + server.address().port);
    listeningHandler(server);
  });

  return server;
}

/* Interpret and emit HTTP status codes from transports using http bearers. Note that socketio does not
 * hit this when connecting with websockets, only polling. If any HTTP status codes are discovered that
 * are redirects, we rewrite connection.config.location accordingly and return a transient failure.
 *
 * @param {URL|DcpURL} url         the location of the service; may be mutated satisfy redirects etc
 * @param {Error}      error       error from underlying socket
 * @param {string}     debugLabel  log message prefix
 */
exports.interpretHttpStatusCode = function httpBearer$$interpretHttpStatusCode(url, error, debugLabel)
{
  switch(error.httpStatus)
  {
    /* HTTP Redirect: adjust the URL and report a transient "failure" */
    case 301: case 302: case 303: case 307: case 308:
    {
      if (error.httpHeaders.hasOwnProperty('location'))
      {
        const u = new URL(error.httpHeaders.location);
        if (u.origin !== url.origin || u.pathname !== url.pathname)
        {
          const newHref = u.origin + u.pathname;
          if (newHref === url.href)
          {
            debugging() && console.debug(debugLabel, `HTTP status ${error.httpStatus}; redirects to same URL (won't try again)`);
            return 'DCP_TRANSPORT_FAILURE_PERMANENT';
          }

          url.href = newHref;
          debugging('connection') && console.debug(debugLabel, `HTTP status ${error.httpStatus}; redirecting to ${url.href}`);
          return 'DCP_TRANSPORT_FAILURE_TRANSIENT';
        }
      }
      debugging('connection') && console.debug(debugLabel, `HTTP status ${error.httpStatus}; missing location header; won't try again`);
      return 'DCP_TRANSPORT_FAILURE_PERMANENT';
    }

    /* These errors are not transient, no point in retrying */
    case 400: case 401: case 403: case 404: case 405: case 406: case 407: case 409: case 410: case 501: case 505:
      debugging('connection') && console.debug(debugLabel, `HTTP status ${error.httpStatus}; won't try again.`);
      return 'DCP_TRANSPORT_FAILURE_PERMANENT';

    /* These errors could be transient, eg server restarting, network outage, etc. retry before giving up on the connection. */
    case 408: case 500: case 502: case 503: case 504:
      debugging('connection') && console.debug(debugLabel, `HTTP status ${error.httpStatus}; will try again`);
      return 'DCP_TRANSPORT_FAILURE_TRANSIENT';

    /* Unknown / unexpected error. Treat as a permanent failure */
    default:
      debugging('connection') && console.debug(debugLabel, `Unknown HTTP status ${error.httpStatus}; won't try again`);
      return 'DCP_TRANSPORT_FAILURE_PERMANENT';
  }

  throw new Error('Reached unreachable code'); // eslint-disable-line no-unreachable
}
